import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { when } from 'mobx';
import type { JSX, ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EPackages } from '@feathr/blackbox';
import { EOnboardingMilestone, type OnboardingTask, Session } from '@feathr/blackbox';
import {
  Button,
  CardV2 as Card,
  Checkbox,
  Icon,
  showError,
  toast,
  Tooltip,
  Well,
} from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { isWretchError } from '@feathr/rachis';
import { useAccount, useRole } from '@feathr/report_components';

import { getMilestoneTasks } from '../OnboardingPage/MilestonePage/MilestoneTasks/MilestoneTasks.types';
import Page from '../Page';
import { renderAccountSetupTasks, renderEmailCampaignTasks } from './DevelopmentQAPage.utils';
import FeathrLiteOnboardingCard from './FeathrLiteOnboardingCard';

function DevelopmentQAPage(): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();
  const { OnboardingTasks } = useStore();
  const [tasks, setTasks] = useState<OnboardingTask[]>([]);
  const [selectedMilestones, setSelectedMilestones] = useState<EOnboardingMilestone[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<OnboardingTask[]>([]);
  const [selectedTaskRequiresAccountSetup, setSelectedTaskRequiresAccountSetup] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [newPackage, setNewPackage] = useState<EPackages | undefined>(undefined);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);

  const { isGlobal } = useRole();

  useEffect(() => {
    async function init(): Promise<void> {
      const tasks = OnboardingTasks.list();
      await when(() => tasks.isLoaded);
      setTasks(tasks.models);
    }

    init();
  }, []);

  async function handleLogOut(): Promise<void> {
    try {
      await Session.logOut(() => {
        window.location.assign(
          `${Session.logInUrl}?from=${encodeURIComponent(window.location.href)}`,
        );
      });
    } catch (error) {
      toast(t('There was an error logging out.'), {
        type: 'error',
      });
    }
  }

  async function handleFactoryReset(): Promise<void> {
    setIsResetting(true);
    toast(t('Account reset in progress. Once complete, you will be logged out.'), {
      type: 'info',
    });

    try {
      const response = await account.resetOnboarding();
      if (isWretchError(response)) {
        throw response.error;
      }

      toast(t('Account reset successfully'), {
        type: 'success',
      });

      await handleLogOut();
    } catch (error) {
      showError(error, t);
    }
  }

  async function handleSelectiveReset(): Promise<void> {
    setIsResetting(true);
    try {
      toast(t('Account reset in progress. Once complete, you will be logged out.'), {
        type: 'info',
      });
      const response = await account.resetOnboarding({
        milestones: selectedMilestones,
        tasks: selectedTasks.map((t) => t.get('_cls')),
      });

      if (isWretchError(response)) {
        throw response.error;
      }

      toast(t('Account reset successfully'), {
        type: 'success',
      });

      await handleLogOut();
    } catch (error) {
      showError(
        t('There was an error resetting the account./n:{{- error}}', {
          error: error instanceof Error ? error.message : error,
        }),
        t,
      );
    }
  }

  async function handleChangePackage(): Promise<void> {
    if (!newPackage) {
      toast(t('New package must be selected to change the package.'), {
        type: 'error',
      });
      return;
    }
    setIsResetting(true);
    toast(t('Package change in progress. Once complete, you will be logged out.'), {
      type: 'info',
    });

    try {
      const response = await account.changeLicensePackage(newPackage, startDate);
      if (isWretchError(response)) {
        throw response.error;
      }

      await handleLogOut();
    } catch (error) {
      showError(error, t);
    }
  }

  function handleSelectInitialAccountSetup(value?: boolean): void {
    setSelectedMilestones((prev) =>
      value
        ? [...prev, EOnboardingMilestone.InitialAccountSetup]
        : [...prev.filter((m) => m !== EOnboardingMilestone.InitialAccountSetup)],
    );

    setSelectedTasks((prev) =>
      prev.filter((t) => t.get('milestone') !== EOnboardingMilestone.InitialAccountSetup),
    );
  }

  function handleSelectAdCampaignSetup(value?: boolean): void {
    setSelectedMilestones((prev) =>
      value
        ? [...prev, EOnboardingMilestone.RetargetingCampaign]
        : [...prev.filter((m) => m !== EOnboardingMilestone.RetargetingCampaign)],
    );

    handleSelectInitialAccountSetup(true);
  }

  function handleSelectEmailCampaignSetup(value?: boolean): void {
    setSelectedMilestones((prev) =>
      value
        ? [...prev, EOnboardingMilestone.SingleSendCampaign]
        : [...prev.filter((m) => m !== EOnboardingMilestone.SingleSendCampaign)],
    );

    setSelectedTasks((prev) =>
      prev.filter((t) => t.get('milestone') !== EOnboardingMilestone.SingleSendCampaign),
    );

    handleSelectInitialAccountSetup(true);
  }

  function handleSelectTask(task: OnboardingTask): void {
    const requiresInitialAccountSetup = (task: OnboardingTask): boolean => {
      return [
        EOnboardingMilestone.RetargetingCampaign,
        EOnboardingMilestone.SingleSendCampaign,
      ].includes(task.get('milestone'));
    };

    if (requiresInitialAccountSetup(task)) {
      setSelectedTaskRequiresAccountSetup(!selectedTasks.includes(task));
      handleSelectInitialAccountSetup(!selectedTasks.includes(task));
    }

    setSelectedTasks((prev) =>
      prev.includes(task) ? [...prev.filter((t) => t !== task)] : [...prev, task],
    );
  }

  const accountMilestones = getMilestoneTasks(EOnboardingMilestone.InitialAccountSetup, t);
  const emailMilestones = getMilestoneTasks(EOnboardingMilestone.SingleSendCampaign, t);

  const selectiveResetTooltip = (function (): string | undefined {
    if (!isGlobal) {
      return t('Global role required');
    }

    if (!selectedMilestones.length && !selectedTasks.length) {
      return t('No milestones or tasks selected');
    }

    return undefined;
  })();

  const initialAccountSetupCheckbox = (): ReactNode => {
    const checkbox = (
      <Checkbox
        disabled={
          !isGlobal ||
          selectedMilestones.some((m) =>
            [
              EOnboardingMilestone.RetargetingCampaign,
              EOnboardingMilestone.SingleSendCampaign,
            ].includes(m),
          ) ||
          selectedTaskRequiresAccountSetup ||
          isResetting
        }
        label={t('Initial Account Setup')}
        onChange={handleSelectInitialAccountSetup}
        value={selectedMilestones.includes(EOnboardingMilestone.InitialAccountSetup)}
      />
    );

    return selectedTaskRequiresAccountSetup ||
      selectedMilestones.some((m) =>
        [
          EOnboardingMilestone.RetargetingCampaign,
          EOnboardingMilestone.SingleSendCampaign,
        ].includes(m),
      ) ? (
      <Tooltip title={t('A selected milestone or task requires this milestone to be completed.')}>
        {checkbox}
      </Tooltip>
    ) : (
      checkbox
    );
  };

  return (
    <Page title={t('Development & QA')}>
      <FeathrLiteOnboardingCard
        handleChangePackage={handleChangePackage}
        handleFactoryReset={handleFactoryReset}
        isResetting={isResetting}
        newPackage={newPackage}
        setNewPackage={setNewPackage}
        setStartDate={setStartDate}
        startDate={startDate}
      />
      <Card disabled={!isGlobal || isResetting}>
        <Card.Header
          description={t(
            'Reset the account with certain milestones and/or tasks already completed.',
          )}
          isCollapsedDefault={true}
          isCollapsible={true}
          title={t('Selective Reset')}
        />
        <Card.Content addVerticalGap={true}>
          <Well label={initialAccountSetupCheckbox()} layout={'vertical'}>
            {renderAccountSetupTasks({
              tasks,
              selectedMilestones,
              selectedTasks,
              isGlobal,
              isResetting,
              accountMilestones,
              handleSelectTask,
            })}
          </Well>
          <Checkbox
            disabled={!isGlobal || isResetting}
            label={t('Ad Campaign Setup')}
            layout={'well'}
            onChange={handleSelectAdCampaignSetup}
            value={selectedMilestones.includes(EOnboardingMilestone.RetargetingCampaign)}
          />

          <Well
            label={
              <Checkbox
                disabled={!isGlobal || isResetting}
                label={t('Email Campaign Setup')}
                onChange={handleSelectEmailCampaignSetup}
                value={selectedMilestones.includes(EOnboardingMilestone.SingleSendCampaign)}
              />
            }
            layout={'vertical'}
          >
            {renderEmailCampaignTasks({
              tasks,
              selectedMilestones,
              selectedTasks,
              isGlobal,
              isResetting,
              emailMilestones,
              handleSelectTask,
            })}
          </Well>
        </Card.Content>
        <Card.Actions>
          <Button
            disabled={
              (!selectedMilestones.length && !selectedTasks.length) || !isGlobal || isResetting
            }
            onClick={handleSelectiveReset}
            prefix={<Icon icon={faArrowsRotate} />}
            tooltip={selectiveResetTooltip}
            type={'danger'}
          >
            {t('Reset')}
          </Button>
        </Card.Actions>
      </Card>
    </Page>
  );
}

export default DevelopmentQAPage;
